import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";

import { MY_PERF_SCROLL_LIST_PB } from "../../Dashboard/layouts";

/** Same greens/yellows/reds as RegionTrackingCard.getBarColor; orange from StartTracking / TerritoryPerformance. */
const BAR_BY_COLOR_BANDING = {
  green: "#00A63E",
  yellow: "#f1c40f  ",
  red: "#e74c3c",
  orange: "#e67e22",
};

const TRACK_BG = "#F1F1F1";
const NEUTRAL_BAR = "#A0A0A0";
const HIGHLIGHT_ROW_BG = "#FFF7ED";

function getBarFillColor(colorBanding) {
  const key = String(colorBanding || "").toLowerCase();
  return BAR_BY_COLOR_BANDING[key] || NEUTRAL_BAR;
}

function PerformanceRow({ row }) {
  const percent = Math.max(0, Number(row.percent) || 0);
  const widthPct = Math.min(100, percent);
  const barColor = getBarFillColor(row.colorBanding);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "110px 1fr 46px",
        alignItems: "center",
        gap: 1.25,
        py: 0.6,
        px: 0.75,
        borderRadius: 1,
        bgcolor: row.highlight ? HIGHLIGHT_ROW_BG : "transparent",
      }}
    >
      <Typography
        sx={{
          fontSize: 13,
          color: row.highlight ? "#D96B04" : "#454545",
          fontWeight: row.highlight ? 700 : 500,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {row.name}
      </Typography>

      <Box
        sx={{
          height: 13,
          borderRadius: 999,
          bgcolor: TRACK_BG,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${widthPct}%`,
            bgcolor: barColor,
            borderRadius: 999,
          }}
        />
      </Box>

      <Typography
        sx={{
          fontSize: 13,
          textAlign: "right",
          color: row.highlight ? "#D96B04" : "#2D2D2D",
          fontWeight: 700,
        }}
      >
        {Math.round(percent)}%
      </Typography>
    </Box>
  );
}

const TERRITORY_ROW_SKELETON_KEYS = [1, 2, 3, 4, 5, 6];

function TerritoryPerformanceCardSkeletonRows() {
  return (
    <Box sx={{ pt: 0.5 }}>
      {TERRITORY_ROW_SKELETON_KEYS.map((k) => (
        <Box
          key={k}
          sx={{
            display: "grid",
            gridTemplateColumns: "110px 1fr 46px",
            alignItems: "center",
            gap: 1.25,
            py: 0.6,
          }}
        >
          <Skeleton
            variant="rounded"
            width={96}
            height={18}
            animation="wave"
            sx={{ borderRadius: 0.5 }}
          />
          <Skeleton
            variant="rounded"
            height={13}
            animation="wave"
            sx={{ borderRadius: 999 }}
          />
          <Skeleton
            variant="rounded"
            width={40}
            height={18}
            animation="wave"
            sx={{ borderRadius: 0.5, justifySelf: "end" }}
          />
        </Box>
      ))}
    </Box>
  );
}

function PerformanceTerritoryTitleRow({ title, titleTooltipText, onExplore }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        width: "100%",
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>
          {title}
        </Typography>
        {!!titleTooltipText && (
          <Tooltip
            title={titleTooltipText}
            placement="right"
            slotProps={{
              popper: {
                modifiers: [{ name: "offset", options: { offset: [0, -10] } }],
              },
              tooltip: {
                sx: {
                  bgcolor: "#FFFFFF",
                  color: "#2D2D2D",
                  border: "1px solid #E6E6E6",
                  boxShadow: "0px 1px 2px 0px #0000000D",
                  whiteSpace: "pre-line",
                },
              },
            }}
          >
            <Box
              aria-label="Performance benchmark information"
              sx={{ lineHeight: 0, display: "inline-flex" }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 15, color: "#A0A0A0" }} />
            </Box>
          </Tooltip>
        )}
      </Box>
      {onExplore && (
        <Button
          variant="text"
          size="small"
          onClick={() => onExplore()}
          sx={{
            textTransform: "none",
            fontSize: 14,
            fontWeight: 600,
            color: "#D96B04",
            minWidth: "auto",
            p: 0,
            flexShrink: 0,
            marginRight: "30px",
          }}
        >
          Explore
        </Button>
      )}
    </Box>
  );
}

/**
 * Reusable territory performance card for both national and regional views.
 */
export default function PerformanceByTerritoryCard({
  title,
  subtitle,
  titleTooltipText,
  rows = [],
  emptyText = "No performance data available.",
  isLoading = false,
  onExplore,
}) {
  const hasRows = Array.isArray(rows) && rows.length > 0;

  return (
    <Card
      variant="outlined"
      aria-busy={isLoading}
      sx={{
        borderRadius: "10px",
        border: "1px solid #EEF2F6",
        boxShadow: "1px 4px 4px 0px #00000008",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <CardContent
        sx={{
          p: 3,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <PerformanceTerritoryTitleRow
            title={title}
            titleTooltipText={titleTooltipText}
            onExplore={onExplore}
          />
        </Box>

        {isLoading ? (
          <>
            <Typography
              sx={{
                mt: 2,
                mb: 1.5,
                fontSize: 14,
                color: "#6B7280",
                flexShrink: 0,
              }}
            >
              {subtitle}
            </Typography>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                maxHeight: 400,
                overflowY: "auto",
                pr: 0.5,
                pb: MY_PERF_SCROLL_LIST_PB,
              }}
            >
              <TerritoryPerformanceCardSkeletonRows />
            </Box>
          </>
        ) : (
          <>
            <Typography
              sx={{
                mt: 2,
                mb: 1.5,
                fontSize: 14,
                color: "#6B7280",
                flexShrink: 0,
              }}
            >
              {subtitle}
            </Typography>

            {hasRows ? (
              <Box
                sx={{
                  flex: "1 1 0",
                  minHeight: 0,
                  maxHeight: 400,
                  overflowY: "auto",
                  pr: 0.5,
                  pb: MY_PERF_SCROLL_LIST_PB,
                }}
              >
                {rows.map((row) => (
                  <PerformanceRow key={row.id} row={row} />
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 120,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ color: "#6B7280", fontSize: 14 }}>
                  {emptyText}
                </Typography>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
